import React from 'react';
import { Container, Row, Col, Card, Tab, Nav } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import FalconCardHeader from '../common/FalconCardHeader';
import { ObjectivesProvider, useObjectives } from 'components/objectives/ObjectivesContext.jsx';
import CreateObjectiveForm from 'components/objectives/CreateObjectiveForm.jsx';
import ObjectivesTable from 'components/objectives/ObjectivesTable.jsx';
import ObjectivesTrackingTable from 'components/objectives/ObjectivesTrackingTable.jsx';
import ObjectiveTreeAllocation from 'components/objectives/ObjectiveTreeAllocation.jsx';
import { useLocation } from 'react-router-dom';

const ObjectivesContent = () => {
  const { objectives, removeObjective } = useObjectives();
  const { pathname } = useLocation();
  const isBranding = pathname?.includes('/branding/');
  const title = isBranding ? 'Objetivos de Branding' : 'Objetivos de Performance';
  
  // Filtrar objetivos según la ruta (branding vs performance)
  const filteredObjectives = React.useMemo(() => {
    const targetCampaignType = isBranding ? 'Branding' : 'Performance';
    
    const filtered = objectives.filter(obj => {
      // Verificar si el objetivo tiene campaignType directamente
      if (obj.campaignType) {
        return obj.campaignType.toLowerCase() === targetCampaignType.toLowerCase();
      }
      // Fallback: verificar por medium (Meta = Performance, otros podrían ser Branding)
      if (obj.medium) {
        if (targetCampaignType === 'Performance') {
          return ['Meta', 'Google'].includes(obj.medium);
        } else {
          return !['Meta', 'Google'].includes(obj.medium);
        }
      }
      // Si no tiene ninguno, incluir en Performance por defecto
      return targetCampaignType === 'Performance';
    });
    
    
    return filtered;
  }, [objectives, isBranding]);
  // Eliminado: refs innecesarios que generan warnings
  // const tabRef = useRef(null);
  // const formRef = useRef(null);
  return (
    <>
      <div className="pb-4">
        {/* Header */}
        <div className="mb-4 card card-body">
          <Row className="align-items-center">
            <Col>
              <h2 className="mb-2">
                <FontAwesomeIcon icon="bullseye" className="me-2 text-primary" />
                {title}
              </h2>
              <h5 className="text-700 fw-semi-bold">
                Gestión y seguimiento de objetivos publicitarios
              </h5>
            </Col>
          </Row>
        </div>

        <Tab.Container defaultActiveKey="tracking">
          <Card className="shadow-sm">
            <FalconCardHeader title="Objetivos y KPIs" />
            <Card.Header>
              <Nav variant="tabs">
                <Nav.Item>
                  <Nav.Link eventKey="tracking">
                    <FontAwesomeIcon icon="chart-line" className="me-1" />
                    Seguimiento
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="create">
                    <FontAwesomeIcon icon="plus" className="me-1" />
                    Crear Objetivo
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="list">
                    <FontAwesomeIcon icon="list" className="me-1" />
                    Ver Objetivos
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="allocation">
                    <FontAwesomeIcon icon="project-diagram" className="me-1" />
                    Asignación
                  </Nav.Link>
                </Nav.Item>
              </Nav>
            </Card.Header>
            <Card.Body>
              <Tab.Content>
                <Tab.Pane eventKey="tracking">
                  {/* kpiResolver: en el futuro podemos conectar KPIs reales (leads, impresiones, etc.) */}
                  <ObjectivesTrackingTable objectives={filteredObjectives} />
                </Tab.Pane>
                <Tab.Pane eventKey="allocation">
                  <ObjectiveTreeAllocationWrapper />
                </Tab.Pane>
                <Tab.Pane eventKey="create">
                  <CreateObjectiveForm campaignFilter={isBranding ? 'Branding' : 'Performance'} />
                </Tab.Pane>
                <Tab.Pane eventKey="list">
                  <ObjectivesTable 
                    rows={filteredObjectives}
                    onEdit={(row) => {
                      // Cambiar a tab crear y cargar data
                      const nav = document.querySelector('[data-rr-ui-event-key="create"]');
                      if (nav) nav.click();
                      // Cargar en el form mediante evento custom
                      const event = new CustomEvent('objective:edit', { detail: row });
                      window.dispatchEvent(event);
                    }}
                    onDelete={(row) => removeObjective(row.id)}
                  />
                </Tab.Pane>
              </Tab.Content>
            </Card.Body>
          </Card>
        </Tab.Container>
      </div>
    </>
  );
};

// Wrapper para integrar selección por evento y pasar el objetivo al componente de árbol
const ObjectiveTreeAllocationWrapper = () => {
  const { objectives } = useObjectives();
  const { pathname } = useLocation();
  const isBranding = pathname?.includes('/branding/');
  const [activeObjective, setActiveObjective] = React.useState(null);

  const filteredObjectives = React.useMemo(() => {
    const target = isBranding ? 'branding' : 'performance';
    return (objectives || []).filter(o => (o.campaignType || '').toLowerCase() === target);
  }, [objectives, isBranding]);

  React.useEffect(() => {
    const handler = (e) => {
      const obj = e?.detail;
      if (obj?.id) setActiveObjective(obj);
    };
    window.addEventListener('objective:allocation-open', handler);
    return () => window.removeEventListener('objective:allocation-open', handler);
  }, []);

  // Si el objetivo activo no está en el filtro (p. ej. cambio de tab), limpiarlo
  React.useEffect(() => {
    if (activeObjective && !filteredObjectives.some(o => o.id === activeObjective.id)) {
      setActiveObjective(null);
    }
  }, [filteredObjectives, activeObjective]);

  return (
    <div>
      {!activeObjective ? (
        <div className="alert alert-info">Selecciona un objetivo en "Ver Objetivos" (🎯) para editar su árbol.</div>
      ) : (
        <ObjectiveTreeAllocation objective={activeObjective} isBranding={isBranding} />
      )}
    </div>
  );
};

const ObjectivesPage = () => (
  <Container fluid className="px-3">
    <ObjectivesProvider>
      <ObjectivesContent />
    </ObjectivesProvider>
  </Container>
);

export default ObjectivesPage; 